import type { ExperimentCategory } from "@andrum/schemas";

/**
 * Structural science metadata only, display strings live in i18n locales.
 */

export type EvidenceLevel = "practice" | "promising" | "emerging";

export type CategoryScienceMeta = {
  category: ExperimentCategory;
  evidenceLevel: EvidenceLevel;
  /** citation URL slots (labels from i18n c1/c2) */
  citationUrls: Array<string | undefined>;
};

export const CATEGORY_SCIENCE_META: CategoryScienceMeta[] = [
  {
    category: "recovery",
    evidenceLevel: "practice",
    citationUrls: [
      undefined,
      "https://pubmed.ncbi.nlm.nih.gov/?term=slow+breathing+heart+rate+variability",
    ],
  },
  {
    category: "presence",
    evidenceLevel: "promising",
    citationUrls: [
      "https://pubmed.ncbi.nlm.nih.gov/?term=mindfulness+meditation+program+meta-analysis",
      undefined,
    ],
  },
  {
    category: "connection",
    evidenceLevel: "practice",
    citationUrls: ["https://pubmed.ncbi.nlm.nih.gov/20668659/", undefined],
  },
  {
    category: "gratitude",
    evidenceLevel: "promising",
    citationUrls: [
      "https://pubmed.ncbi.nlm.nih.gov/?term=Emmons+McCullough+gratitude",
      undefined,
    ],
  },
  {
    category: "movement",
    evidenceLevel: "practice",
    citationUrls: [
      "https://www.who.int/news-room/fact-sheets/detail/physical-activity",
      undefined,
    ],
  },
  {
    category: "meaning",
    evidenceLevel: "promising",
    citationUrls: [
      undefined,
      "https://pubmed.ncbi.nlm.nih.gov/?term=eudaimonic+well-being+meaning",
    ],
  },
];

export const CATEGORY_SCIENCE = CATEGORY_SCIENCE_META;

export function scienceForCategory(
  category: ExperimentCategory
): CategoryScienceMeta {
  return (
    CATEGORY_SCIENCE_META.find((c) => c.category === category) ??
    CATEGORY_SCIENCE_META[0]!
  );
}

export type ExperimentScienceMeta = {
  slug: string;
  evidenceLevel: EvidenceLevel;
};

export const EXPERIMENT_SCIENCE_META: Record<string, ExperimentScienceMeta> = {
  "two-minute-breath": { slug: "two-minute-breath", evidenceLevel: "practice" },
  "three-gratitudes": { slug: "three-gratitudes", evidenceLevel: "promising" },
  "water-and-window": { slug: "water-and-window", evidenceLevel: "emerging" },
  "call-someone-you-miss": {
    slug: "call-someone-you-miss",
    evidenceLevel: "practice",
  },
  "stretch-shoulders": { slug: "stretch-shoulders", evidenceLevel: "practice" },
  "walk-without-phone": {
    slug: "walk-without-phone",
    evidenceLevel: "promising",
  },
  "one-kind-sentence": {
    slug: "one-kind-sentence",
    evidenceLevel: "promising",
  },
  "thank-someone": { slug: "thank-someone", evidenceLevel: "promising" },
  "evening-wind-down": {
    slug: "evening-wind-down",
    evidenceLevel: "practice",
  },
  "deep-connection-letter": {
    slug: "deep-connection-letter",
    evidenceLevel: "emerging",
  },
};

/** @deprecated use EXPERIMENT_SCIENCE_META + i18n */
export const EXPERIMENT_SCIENCE = EXPERIMENT_SCIENCE_META;

export function scienceForSlug(
  slug: string,
  category: ExperimentCategory
): ExperimentScienceMeta {
  return (
    EXPERIMENT_SCIENCE_META[slug] ?? {
      slug,
      evidenceLevel: scienceForCategory(category).evidenceLevel,
    }
  );
}
